function coerceId(videoLike) {
  return (
    videoLike?.id ??
    videoLike?.video_id ??
    videoLike?.videoId ??
    videoLike?.video_analysis_id ??
    videoLike?.videoAnalysisId ??
    null
  );
}

function slugify(text) {
  if (!text || typeof text !== 'string') {
    return '';
  }
  const lower = text.toLowerCase();
  const cleaned = lower
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!cleaned) {
    return '';
  }

  return cleaned.replace(/\s+/g, '-').replace(/-+/g, '-');
}

function buildFallbackSlug(videoLike) {
  const id = coerceId(videoLike);
  if (id === null || id === undefined) {
    return null;
  }
  const idString = String(id).trim();
  if (!idString) {
    return null;
  }

  const candidateText =
    videoLike?.simple_description ||
    videoLike?.simpleDescription ||
    videoLike?.overview ||
    videoLike?.caption ||
    '';

  const slugPart = slugify(candidateText);
  if (!slugPart) {
    return idString;
  }

  return `${idString}-${slugPart}`;
}

function normalizeSlug(videoLike) {
  if (!videoLike || typeof videoLike !== 'object') {
    return { video: videoLike, slug: null };
  }

  const rawSlug = videoLike.video_slug ?? videoLike.videoSlug;
  const id = coerceId(videoLike);
  const idString = id !== null && id !== undefined ? String(id).trim() : null;

  const cleanedSlug =
    typeof rawSlug === 'string' ? rawSlug.trim() : '';

  const needsFallback =
    !cleanedSlug ||
    /^[-]+$/.test(cleanedSlug);

  if (!needsFallback) {
    return {
      video: videoLike.video_slug === cleanedSlug
        ? videoLike
        : { ...videoLike, video_slug: cleanedSlug },
      slug: cleanedSlug
    };
  }

  const fallback = buildFallbackSlug(videoLike);
  if (!fallback) {
    return {
      video: cleanedSlug
        ? { ...videoLike, video_slug: cleanedSlug }
        : videoLike,
      slug: cleanedSlug || null
    };
  }

  return {
    video: { ...videoLike, video_slug: fallback },
    slug: fallback
  };
}

export function buildVideoUrl(videoLike) {
  const { slug } = normalizeSlug(videoLike);
  if (slug) {
    return `/video/${slug}`;
  }
  return null;
}

export function ensureVideoSlug(videoLike) {
  const { video } = normalizeSlug(videoLike);
  return video;
}

const SUPABASE_OBJECT_PREFIX = 'https://obchfdkehwfbicjxlxpu.supabase.co/storage/v1/object/public/';

export const THUMBNAIL_WIDTHS = Object.freeze({
  avatar: 100,
  tooltip: 200,
  collection: 250,
  compactCard: 300,
  card: 400,
  taxonomyDetail: 480,
  heroCollage: 600,
  videoPlayer: 1080,
});

export function optimizeThumbnailUrl(url, width = 400) {
  if (!url || typeof url !== 'string') return url;

  if (url.startsWith('/api/img?')) {
    try {
      const params = new URLSearchParams(url.split('?')[1]);
      const path = params.get('path');
      if (path) {
        return `/api/img?path=${encodeURIComponent(path)}&w=${width}`;
      }
    } catch (e) {
      // fall through
    }
    return url;
  }

  if (!url.startsWith(SUPABASE_OBJECT_PREFIX)) return url;
  const bucketAndPath = url.substring(SUPABASE_OBJECT_PREFIX.length);
  return `/api/img?path=${encodeURIComponent(bucketAndPath)}&w=${width}`;
}

function withOptimizedThumbnail(videoLike, width = THUMBNAIL_WIDTHS.collection) {
  if (!videoLike || typeof videoLike !== 'object') return videoLike;
  const raw = videoLike.thumbnail || videoLike.thumbnail_url || videoLike.thumbnailUrl;
  if (!raw || typeof raw !== 'string') {
    return videoLike;
  }
  const optimized = optimizeThumbnailUrl(raw, width);
  if (optimized === raw) return videoLike;
  return { ...videoLike, thumbnail: optimized };
}

export function normalizeVideoCollection(collection, thumbnailWidth = THUMBNAIL_WIDTHS.collection) {
  if (Array.isArray(collection)) {
    return collection.map((item) => withOptimizedThumbnail(ensureVideoSlug(item), thumbnailWidth));
  }
  if (collection && typeof collection === 'object') {
    return withOptimizedThumbnail(ensureVideoSlug(collection), thumbnailWidth);
  }
  return collection;
}
